import { apiErrorMessage } from '@/utils/apiError';
import { createLatestGuard } from '@/utils/latestRequest';
import type {
  PvpSyncDto,
  PvpSyncTotals,
  PvpSyncMetric,
  PvpSyncPlatform,
  PvpSyncQuery,
  PvpSyncRole,
} from '@/types/pvp';

// ---- Range and filters ----

export type SyncPreset = '1h' | '24h' | '7d';

const HOUR_MS = 60 * 60 * 1000;

export const SYNC_PRESETS: Array<{ label: string; value: SyncPreset; ms: number }> = [
  { label: '1 h', value: '1h', ms: HOUR_MS },
  { label: '24 h', value: '24h', ms: 24 * HOUR_MS },
  { label: '7 d', value: '7d', ms: 7 * 24 * HOUR_MS },
];

export const DEFAULT_PRESET: SyncPreset = '24h';

/** The largest whole number the endpoint takes for `simVersion` and `bv`. */
export const INT_MAX = 2147483647;

/** The longest `app` the endpoint takes. */
export const APP_MAX = 40;

export const PLATFORMS: PvpSyncPlatform[] = ['android', 'ios', 'editor'];

/** The filters as typed: empty = not set. */
export interface SyncFilters {
  simVersion?: number | null;
  app?: string;
  bv?: number | null;
  platform?: PvpSyncPlatform;
}

/**
 * The query for a preset counted back from `now`: ISO from/to, plus only the
 * filters that are set. `now` is read when a preset is chosen or Refresh is
 * pressed, so a refetch always covers the latest hour/day/week.
 */
export function syncQuery(preset: SyncPreset, filters: SyncFilters, now: Date): PvpSyncQuery {
  const ms = (SYNC_PRESETS.find((p) => p.value === preset) ?? SYNC_PRESETS[1]).ms;
  const query: PvpSyncQuery = {
    from: new Date(now.getTime() - ms).toISOString(),
    to: now.toISOString(),
  };
  if (typeof filters.simVersion === 'number') query.simVersion = filters.simVersion;
  const app = filters.app?.trim();
  if (app) query.app = app.slice(0, APP_MAX);
  if (typeof filters.bv === 'number') query.bv = filters.bv;
  if (filters.platform) query.platform = filters.platform;
  return query;
}

// ---- Numbers ----

/** A number for a cell; "—" for no samples (null, missing, not finite), never 0. */
export function fmtNum(value: number | null | undefined, maxDigits = 2): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-IN', { maximumFractionDigits: maxDigits });
}

/** A phone as "app build platform", with a dash for what the row does not say. */
export const phoneLabel = (
  app: string | null | undefined,
  bv: number | null | undefined,
  platform: string | null | undefined
): string => [app || '—', bv == null ? '—' : `b${bv}`, platform || '—'].join(' ');

/** The data-quality line, or null when nothing is above 0 (so a clean range shows no line). */
export function dataQualityLine(totals: PvpSyncTotals): string | null {
  const n = (v: number | null | undefined) => (typeof v === 'number' && v > 0 ? v : 0);
  const unplaced = n(totals.unplaced);
  const otherKeys = n(totals.otherKeys);
  const cutMetrics = n(totals.keysCut?.metrics);
  const cutTexts = n(totals.keysCut?.texts);
  if (unplaced + otherKeys + cutMetrics + cutTexts === 0) return null;
  return (
    `Unplaced reports: ${fmtNum(unplaced, 0)} · Keys beyond the per-match cap: ${fmtNum(otherKeys, 0)}` +
    ` · Keys left out of this answer: metrics ${fmtNum(cutMetrics, 0)}, texts ${fmtNum(cutTexts, 0)}`
  );
}

/** The note under a metric/text table when keys were cut; null when none were. */
export function keysCutNote(cut: number | null | undefined): string | null {
  if (typeof cut !== 'number' || !(cut > 0)) return null;
  return `${fmtNum(cut, 0)} more key rows not shown (a row is one key for one role; only the known keys and the 24 most used others per role are listed)`;
}

/** The tooltip of the by-app desync column. */
export const BY_APP_DESYNC_TIP =
  "Each build's phones are charged only their own mismatches, per 1,000 balls that group's phones played.";

/** The app of a by-app row: the real name, or null when the phone sent none (never the word "unknown"). */
export const appName = (app: string | null | undefined): string | null =>
  typeof app === 'string' && app !== '' ? app : null;

// ---- Metrics ----

/** The metric keys the page explains, in the order it lists them. */
export const KNOWN_METRICS = [
  'startErrMs',
  'slackMs',
  'holdMs',
  'playoutMs',
  'rttMs',
  'offsetMs',
  'gapMaxMs',
] as const;

/** One line per key: what the number means. */
export const METRIC_HELP: Record<string, string> = {
  startErrMs: "how far the phone's ball start was from the server's startAt (negative = early)",
  slackMs:
    "how much earlier than needed the batter's swing reached the watcher (positive = in time)",
  holdMs: 'how long the watcher held the ball waiting for inputs',
  playoutMs: "the watcher's playout delay",
  rttMs: 'the round trip from the clock estimator at ball start',
  offsetMs: "the phone's clock offset at ball start",
  gapMaxMs: 'the largest main-thread gap during the ball',
};

/** Text values (counted, not measured). */
export const KNOWN_TEXTS = ['quality'] as const;

export const TEXT_HELP: Record<string, string> = {
  quality: "the phone's own net-quality bucket",
};

const ROLES: PvpSyncRole[] = ['batter', 'watcher'];

function keyRank(key: string, known: readonly string[]): number {
  const at = known.indexOf(key);
  return at === -1 ? known.length : at;
}

function compare(
  a: { key: string; role: PvpSyncRole },
  b: { key: string; role: PvpSyncRole },
  known: readonly string[]
): number {
  const byRank = keyRank(a.key, known) - keyRank(b.key, known);
  if (byRank !== 0) return byRank;
  if (a.key !== b.key) return a.key < b.key ? -1 : 1;
  return ROLES.indexOf(a.role) - ROLES.indexOf(b.role);
}

/** Known keys first in their order, then the rest alphabetically; batter before watcher. */
export const sortMetrics = (rows: PvpSyncMetric[]): PvpSyncMetric[] =>
  [...rows].sort((a, b) => compare(a, b, KNOWN_METRICS));

export const sortTexts = <T extends { key: string; role: PvpSyncRole }>(rows: T[]): T[] =>
  [...rows].sort((a, b) => compare(a, b, KNOWN_TEXTS));

/** The backend sums the values beyond the top 20 of a key under this name. */
export const OTHER_KEY = '_other';

/** Text values by count, biggest first, with the `_other` bucket always last and labelled "other". */
export function textEntries(
  values: Record<string, number>
): Array<{ label: string; count: number }> {
  const rows = Object.entries(values).map(([value, count]) => ({ value, count }));
  const named = rows.filter((r) => r.value !== OTHER_KEY).sort((a, b) => b.count - a.count);
  const other = rows.filter((r) => r.value === OTHER_KEY);
  return [...named, ...other].map((r) => ({
    label: r.value === OTHER_KEY ? 'other' : r.value,
    count: r.count,
  }));
}

// ---- Loading ----

export interface SyncState {
  data: PvpSyncDto | null;
  loading: boolean;
  error: string | null;
}

type FetchSync = (query: PvpSyncQuery) => Promise<{ data: PvpSyncDto }>;

/**
 * Loads the sync answer and reports each step to `apply`. Only the newest call
 * applies its answer, so a slow older range cannot overwrite a newer one.
 */
export function createSyncLoader(fetchSync: FetchSync, apply: (patch: Partial<SyncState>) => void) {
  const guard = createLatestGuard();
  return {
    async load(preset: SyncPreset, filters: SyncFilters, now: Date = new Date()): Promise<void> {
      const isLatest = guard.begin();
      apply({ loading: true, error: null });
      try {
        const response = await fetchSync(syncQuery(preset, filters, now));
        if (!isLatest()) return;
        apply({ loading: false, data: response.data });
      } catch (e) {
        if (!isLatest()) return;
        apply({
          loading: false,
          data: null,
          error: apiErrorMessage(e, 'Failed to load the PvP sync numbers'),
        });
      }
    },
  };
}

// ---- Clipboard ----

/** Copies `text`; false when the browser has no clipboard or refuses. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
